import React, { useEffect } from 'react';
import {
  Card, Form, Input, Button, message, Spin, Space, Row, Col
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetLocationByIdQuery, useUpdateLocationMutation } from './location.service';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';

const LocationEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { data, isLoading } = useGetLocationByIdQuery(id);
  const [updateLocation, { isLoading: isUpdating }] = useUpdateLocationMutation();

  const location = data?.data;

  useEffect(() => {
    if (location) {
      form.setFieldsValue({
        name: location.name,
        street: location.address?.street || '',
        city: location.address?.city || '',
        state: location.address?.state || '',
        postalCode: location.address?.postalCode || '',
        country: location.address?.country || 'GB',
        phone: location.phone || '',
        email: location.email || '',
        website: location.website || '',
      });
    }
  }, [location, form]);

  const onFinish = async (values) => {
    try {
      const address = {
        street: values.street,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country || 'GB',
      };

      await updateLocation({
        id,
        data: {
          name: values.name,
          address,
          phone: values.phone,
          email: values.email,
          website: values.website,
        },
      }).unwrap();

      message.success('Location updated successfully');
      navigate('/locations');
    } catch (error) {
      message.error(error.data?.message || 'Failed to update location');
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      bordered={false}
      title={
        <Space>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/locations')}
          />
          <span>Edit Location - {location?.name || ''}</span>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="name"
          label="Location Name"
          rules={[{ required: true, message: 'Please enter location name' }]}
        >
          <Input placeholder="e.g., London Branch" size="large" />
        </Form.Item>

        <Form.Item
          name="street"
          label="Street Address"
          rules={[{ required: true, message: 'Please enter street address' }]}
        >
          <Input placeholder="123 Main Street" size="large" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <Input placeholder="London" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="state" label="State/County">
              <Input placeholder="Greater London" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="postalCode" label="Postal Code" rules={[{ required: true }]}>
              <Input placeholder="SW1A 1AA" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="country" label="Country" rules={[{ required: true }]}>
              <Input placeholder="United Kingdom" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="phone" label="Phone Number">
              <Input placeholder="+44 20 1234 5678" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
              <Input placeholder="info@example.com" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="website" label="Website">
          <Input placeholder="https://example.com" size="large" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={isUpdating}
              size="large"
              style={{ background: '#2d5a3d', borderColor: '#2d5a3d' }}
            >
              Save Changes
            </Button>
            <Button size="large" onClick={() => navigate('/locations')}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default LocationEdit;